import type {
  CmmdProviderAccountInventoryAccount,
  CmmdProviderUsageModelWindow,
  CmmdProviderUsageSnapshot,
  ModelSelection,
  ThreadProviderRateLimitStatus,
} from "@cmmd-forge/contracts";

import type { ProviderUsageAccountView } from "~/usage/useProviderUsage";
import { findProviderAccountByIdentity } from "@cmmd-forge/shared/providerAccountIdentity";
import { resolveEffectiveAccountId } from "./rateLimitBannerSelection";

/** The providers whose threads draw on a personal Connected Subscription. */
export type SubscriptionProvider = "codex" | "claudeAgent";

export function isSubscriptionProvider(provider: string): provider is SubscriptionProvider {
  return provider === "codex" || provider === "claudeAgent";
}

export type PlanUsageWindowKind = "fiveHour" | "sevenDay" | "model";
export type PlanUsageWindowSource = "thread" | "snapshot";

export interface PlanUsageWindowView {
  readonly kind: PlanUsageWindowKind;
  readonly label: "5-hour" | "Weekly" | `Weekly · ${string}`;
  /** Set only for `kind: "model"` — the model this per-model weekly window reports on. */
  readonly model?: string;
  readonly usedPercent: number;
  readonly remainingPercent: number;
  readonly resetsAt: string;
  readonly source: PlanUsageWindowSource;
}

/** What the composer's context popover shows about the thread's plan. */
export interface ComposerPlanUsage {
  readonly providerLabel: "Codex" | "Claude";
  readonly accountLabel: string | null;
  readonly planType: string | null;
  readonly windows: ReadonlyArray<PlanUsageWindowView>;
  /** When the cached snapshot behind any shown window was observed; null when every window is live. */
  readonly observedAt: string | null;
  readonly stale: boolean;
}

interface WindowCandidate {
  readonly usedPercent: number;
  readonly resetsAtMs: number;
  readonly resetsAt: string;
  readonly source: PlanUsageWindowSource;
}

// "model" has no fixed label — it is built per instance as `Weekly · ${model}`.
const WINDOW_LABEL: Record<"fiveHour" | "sevenDay", PlanUsageWindowView["label"]> = {
  fiveHour: "5-hour",
  sevenDay: "Weekly",
};

const PROVIDER_LABEL: Record<SubscriptionProvider, ComposerPlanUsage["providerLabel"]> = {
  codex: "Codex",
  claudeAgent: "Claude",
};

export function resolveComposerUsageAccount(input: {
  readonly modelSelection: ModelSelection;
  readonly usageAccounts: ReadonlyArray<ProviderUsageAccountView>;
}): {
  readonly provider: SubscriptionProvider;
  readonly effectiveAccountId: string | null;
  readonly accountView: ProviderUsageAccountView | undefined;
} | null {
  const provider = input.modelSelection.provider;
  if (!isSubscriptionProvider(provider)) return null;

  const effectiveAccountId = resolveEffectiveAccountId({
    provider,
    currentProviderAccountId:
      "providerAccountId" in input.modelSelection
        ? input.modelSelection.providerAccountId
        : undefined,
    accounts: input.usageAccounts.map((entry) => entry.account),
  });
  return {
    provider,
    effectiveAccountId,
    accountView: input.usageAccounts.find(
      (entry) =>
        entry.account.provider === provider &&
        entry.account.providerAccountId === effectiveAccountId,
    ),
  };
}

function toCandidate(
  window: { readonly usedPercent: number; readonly resetsAt: string } | null | undefined,
  source: PlanUsageWindowSource,
  nowMs: number,
): WindowCandidate | null {
  if (!window) return null;
  const resetsAtMs = new Date(window.resetsAt).getTime();
  // A window whose reset has passed describes a period that is over. Its use
  // is zero now, and showing the old share would report headroom that is not
  // there any more, or a wall that is not.
  if (Number.isNaN(resetsAtMs) || resetsAtMs <= nowMs) return null;
  return { usedPercent: window.usedPercent, resetsAtMs, resetsAt: window.resetsAt, source };
}

/**
 * Timestamp-free merge of a cached and a live reading of the same window.
 *
 * Thread windows carry no observed time, so recency is read off the window
 * itself: a later reset instant is a newer period and wins outright, and within
 * one period use only ever grows, so the higher share is the later reading.
 * Ties go to the thread, whose figure came from this conversation's own turn.
 */
function pickWindow(candidates: ReadonlyArray<WindowCandidate | null>): WindowCandidate | null {
  return (
    candidates
      .filter((candidate): candidate is WindowCandidate => candidate !== null)
      .toSorted(
        (a, b) =>
          b.resetsAtMs - a.resetsAtMs ||
          b.usedPercent - a.usedPercent ||
          (a.source === "thread" ? -1 : 1) - (b.source === "thread" ? -1 : 1),
      )[0] ?? null
  );
}

function accountLabel(account: CmmdProviderAccountInventoryAccount | undefined): string | null {
  if (!account) return null;
  return account.accountDisplayName || account.accountEmail || null;
}

function findThreadEntry(input: {
  readonly provider: SubscriptionProvider;
  readonly effectiveAccountId: string | null;
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
}): ThreadProviderRateLimitStatus | undefined {
  const statusProvider = input.provider === "claudeAgent" ? "claude" : "codex";
  const providerAccounts = input.accounts.filter((account) => account.provider === input.provider);
  return input.rateLimitStatus?.find((entry) => {
    if (entry.provider !== statusProvider) return false;
    const reported = entry.providerAccountId ?? null;
    const reportedCanonicalId =
      reported === null
        ? null
        : (findProviderAccountByIdentity(providerAccounts, reported)?.providerAccountId ??
          reported);
    return reportedCanonicalId === null || reportedCanonicalId === input.effectiveAccountId;
  });
}

function remainingPercentOf(usedPercent: number): number {
  return Math.max(0, Math.min(100, Math.round(100 - usedPercent)));
}

/** The five-hour and weekly rows, each merged from the cached snapshot and the live thread window. */
function collectStandardWindows(input: {
  readonly snapshot: CmmdProviderUsageSnapshot | null;
  readonly threadEntry: ThreadProviderRateLimitStatus | undefined;
  readonly nowMs: number;
}): ReadonlyArray<PlanUsageWindowView> {
  const windows: PlanUsageWindowView[] = [];
  for (const kind of ["fiveHour", "sevenDay"] as const) {
    const picked = pickWindow([
      toCandidate(input.snapshot?.[kind], "snapshot", input.nowMs),
      toCandidate(input.threadEntry?.[kind], "thread", input.nowMs),
    ]);
    if (!picked) continue;
    windows.push({
      kind,
      label: WINDOW_LABEL[kind],
      usedPercent: picked.usedPercent,
      remainingPercent: remainingPercentOf(picked.usedPercent),
      resetsAt: picked.resetsAt,
      source: picked.source,
    });
  }
  return windows;
}

/**
 * Per-model weekly windows come from the cached snapshot only: no thread
 * window carries a model breakdown, so each is always source "snapshot" and
 * follows the same expired-window drop rule as the standard rows.
 */
function collectModelWindows(
  modelWindows: ReadonlyArray<CmmdProviderUsageModelWindow>,
  nowMs: number,
): ReadonlyArray<PlanUsageWindowView> {
  return modelWindows
    .toSorted((a, b) => a.model.localeCompare(b.model))
    .flatMap((modelWindow) => {
      const picked = toCandidate(modelWindow, "snapshot", nowMs);
      if (!picked) return [];
      return [
        {
          kind: "model" as const,
          label: `Weekly · ${modelWindow.model}` as const,
          model: modelWindow.model,
          usedPercent: picked.usedPercent,
          remainingPercent: remainingPercentOf(picked.usedPercent),
          resetsAt: picked.resetsAt,
          source: picked.source,
        },
      ];
    });
}

export function selectComposerPlanUsage(input: {
  readonly modelSelection: ModelSelection;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
  readonly usageAccounts: ReadonlyArray<ProviderUsageAccountView>;
  readonly nowMs: number;
}): ComposerPlanUsage | null {
  const resolution = resolveComposerUsageAccount(input);
  if (!resolution) return null;
  const { provider, effectiveAccountId, accountView } = resolution;
  const snapshot: CmmdProviderUsageSnapshot | null = accountView?.snapshot ?? null;
  const threadEntry = findThreadEntry({
    provider,
    effectiveAccountId,
    accounts: input.usageAccounts.map((entry) => entry.account),
    rateLimitStatus: input.rateLimitStatus,
  });
  if (!accountView && !threadEntry) return null;

  const standard = collectStandardWindows({ snapshot, threadEntry, nowMs: input.nowMs });
  const models = collectModelWindows(snapshot?.modelWindows ?? [], input.nowMs);
  const windows = [...standard, ...models];
  const snapshotShown =
    windows.some((window) => window.source === "snapshot") || snapshot?.credits != null;

  return {
    providerLabel: PROVIDER_LABEL[provider],
    accountLabel: accountLabel(accountView?.account),
    planType: accountView?.account.planType ?? snapshot?.planType ?? null,
    windows,
    observedAt: snapshotShown && snapshot ? snapshot.observedAt : null,
    stale: snapshotShown && snapshot ? snapshot.stale : false,
  };
}
